import { Component, createRef, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import {
  DataGrid,
  Column,
  Editing,
  Scrolling,
  Selection,
  SearchPanel,
  Lookup,
  ColumnChooser,
  Grouping,
  GroupPanel,
  FilterPanel,
  HeaderFilter,
  Search,
  FilterRow,
  Button,
} from 'devextreme-react/data-grid';
import SelectBox from 'devextreme-react/select-box';
import { EditMemberPopup } from '../../pages/members/edit-member-popup';
import { EditNonMemberPopup } from '../../pages/non-members/edit-non-member-popup';
import { CallLogPopup, subscribeCallLogSaved } from '~/components/call-log-popup/call-log-popup';
import {
  calcGridHeight,
  setDateTimeInlineEditing,
  renderPreferredFirstNameGridCell,
  convertToTZ,
} from '~/helpers/ui';
import { CreateAuthorizedDataSource, getMemberById, loadMembersLookupDataAsync, getNonMemberById, callStatusData } from '~/helpers/data';
import { appConst } from '~/app-const';
import { CallLogLookupsContext } from '~/call-log-lookups-context';
import { CategorySelectorContent } from '~/components';
import { GridLayout } from '~/components/grid-layout-component/grid-layout';

import './person-call-log.scss';

const callLogsUrl = `${window.env.apiEndpoint}/api/v1/CallLogs`;

class PersonCallLogGridClass extends Component {
  constructor(props) {
    super(props);

    this.renderMemberEditPopUp = this.renderMemberEditPopUp.bind(this);

    this.state = {
      currentFirstName: '',
      showMemberForm: false,
      showNonMemberForm: false,
      callLogPopups: [],
    };


    this.actionsData = CreateAuthorizedDataSource({ loadUrl: `${window.env.apiEndpoint}/api/v1/CallLogActions/CallLogActionsLookup` }, null, 'Value', null, 'Header');
    this.usersData = CreateAuthorizedDataSource({ loadUrl: `${window.env.apiEndpoint}/api/v1/Users/Get` }, null, 'Id');

    this._categoryFilter = [];
    this.onEditorPreparing = this.onEditorPreparing.bind(this);
    this.selectBoxActionColumnTemplate = this.selectBoxActionColumnTemplate.bind(this);
    this.selectBoxSatusColumnTemplate = this.selectBoxSatusColumnTemplate.bind(this);
    this.iSPersonGrid = this.iSPersonGrid.bind(this);
    this.callLogsGridRef = createRef();
    this.statusData = callStatusData;
  }

  dataSource = () => {
    return CreateAuthorizedDataSource(
      {
        loadUrl: this.props.isInvolvedCalls
        ? `${callLogsUrl}/GetCallLogsByInvolvedPerson`
        : (this.props.Id ? `${callLogsUrl}/GetCallLogsByPerson` : `${callLogsUrl}/Get`),
        key: 'Id',
        updateUrl: `${callLogsUrl}/Put`,
        deleteUrl: `${callLogsUrl}/Delete`,
      },
      {
        load: {
          timeZone: window.env.timeZone,
          personId: this.props.Id,
        },
        update: { timeZone: window.env.timeZone },
      }, 'Id',
    );
  }

  setGridHeight() {
    const instance = this.callLogsGridRef.current.instance();
    instance.option('height', calcGridHeight(instance.element()));
  }

  onEditorPreparing(e) {
    if (e.dataField === 'Details' && e.parentType !== 'filterRow') {
      e.editorName = 'dxTextArea';
      e.editorOptions.height = 100;
    }
    if (e.parentType !== 'filterRow' && (e.dataField === 'Type'
      || e.dataField === 'FirstName'
      || e.dataField === 'LastName'
      || e.dataField === 'School'
      || e.dataField === 'PreferredFirstName')) {
      e.editorOptions.readOnly = true;
    }
    setDateTimeInlineEditing(['CallDate'], e);

    if (e.parentType === 'filterRow' && e.dataField === 'CategoriesDisplay') {
      e.editorName = 'dxDropDownBox';
      e.editorOptions = {
        placeholder: 'Filter by categories...',
        showClearButton: true,
        dropDownOptions: { width: 400 },
        contentTemplate: (args, contentElement) => {
          const el = contentElement?.get ? contentElement.get(0) : contentElement;
          const root = createRoot(el);
          root.render(
            <CategorySelectorContent
              categories={this.props.lookups?.categories ?? []}
              subcategories={this.props.lookups?.subcategories ?? []}
              selectedCategories={[]}
              onSelectionChanged={(selection) => {
                this._categoryFilter = selection ?? [];
                const json = selection?.length ? JSON.stringify(selection) : null;
                this.callLogsGridRef.current?.instance().columnOption('CategoriesDisplay', 'filterValue', json);
              }}
            />
          );
        },
        onValueChanged: (args) => { e.setValue(args.value); },
      };
    }
  }

  onSelectBoxValueChanged(cell, e) {
    cell.setValue(e.value);
  }

  selectBoxSatusColumnTemplate(cell) {
    return (
      <SelectBox
        defaultValue={cell.value}
        {...cell.column.lookup}
        onValueChanged={(e) => this.onSelectBoxValueChanged(cell, e)}
        itemRender={this.itemRender}
        dataSource={this.statusData}
      />
    );
  }

  selectBoxActionColumnTemplate(cell) {
    return (
      <SelectBox
        {...appConst.defaultSelectBoxOptions}
        defaultValue={cell.value}
        {...cell.column.lookup}
        onValueChanged={(e) => this.onSelectBoxValueChanged(cell, e)}
        itemRender={this.itemRender}
        grouped
        dataSource={this.actionsData}
      />
    );
  }

  renderDetails(cellData) {
    return (
      <div className="detail-container">{cellData.data.Details}</div>
    );
  }

  iSPersonGrid() {
    return (!!this.props.Id);
  }

  async openPersonEditForm(data) {
    const lookupData = await loadMembersLookupDataAsync();
    this.setState({ membersLookupData: lookupData });
    if (data.Type === 'Member') {
      getMemberById(data.PersonId).then((result) => {
        this.setState({ showMemberForm: true, rowMemberData: result });
      });
    } else if (data.Type === 'NonMember') {
      getNonMemberById(data.PersonId).then((result) => {
        this.setState({ showNonMemberForm: true, rowNonMemberData: result });
      });
    }
  }

  renderPersonNameLink(content, data) {
    return (
      <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.openPersonEditForm(data); }}>
        {content}
      </a>
    );
  }

  componentDidMount() {
    if (!this.iSPersonGrid() && this.callLogsGridRef?.current) {
      const instance = this.callLogsGridRef.current.instance();
      instance.option('height', calcGridHeight(instance.element()));
    }
    this._unsubscribeRefresh = subscribeCallLogSaved(() => {
      this.callLogsGridRef.current?.instance().refresh();
    });
  }

  componentWillUnmount() {
    this._unsubscribeRefresh?.();
  }

  renderMemberEditPopUp() {
    const { rowMemberData, showMemberForm, membersLookupData } = this.state;
    if (showMemberForm) {
      return (
        <EditMemberPopup
          showMemberEditForm={showMemberForm}
          rowMemberData={rowMemberData}
          memberAddNewMode={false}
          membersLookupData={membersLookupData}
          hideMemberEditPopup={() => {
            this.setState({ showMemberForm: false });
          }}
          selectedTabIndex={5}
        />
      );
    }
  }

  renderNonMemberEditPopUp() {
    const { rowNonMemberData, showNonMemberForm, membersLookupData } = this.state;
    if (showNonMemberForm) {
      return (
        <EditNonMemberPopup
          showNonMemberEditForm={showNonMemberForm}
          rowMemberData={rowNonMemberData}
          nonMemberAddNewMode={false}
          hideNonMemberEditPopup={() => {
            this.setState({ showNonMemberForm: false });
          }}
          membersLookupData={membersLookupData}
          selectedTabIndex={1}
        />
      );
    }
  }

  render() {
    return (
      <>
        <GridLayout
          layoutKey="CallLogs"
          gridName="callLogGrid"
          pageName="CallLogPage"
          gridRef={() => { return this.callLogsGridRef.current.instance() }}
        />
        <DataGrid
          id="callLogsGrid"
          ref={this.callLogsGridRef}
          dataSource={this.dataSource()}
          {...appConst.defaultGridOptions}
          onEditorPreparing={this.onEditorPreparing}
          onRowDblClick={(e) => {
            this.setState((prev) => ({
              callLogPopups: [...prev.callLogPopups, { key: Date.now(), Id: e.key }],
            }));
          }}
          onInitialized={(e) => {
            if (this.iSPersonGrid()) {
              e.component.option('height', '100%');
            } else {
              e.component.option('height', calcGridHeight(e.component.element().id));
            }
          }}
        >
          <Scrolling
            mode="virtual"
            rowRenderingMode="virtual"
          />
          <Selection mode="multiple" />
          <SearchPanel
            visible
            width={240}
            placeholder="Search..."
          />
          <Editing
            mode="popup"
            allowAdding={false}
            allowDeleting
            allowUpdating={false}
          />
          <ColumnChooser
            enabled
            mode="dragAndDrop"
          />
          <Grouping
            allowCollapsing
            autoExpandAll={false}
            contextMenuEnabled
            expandMode="rowClick"
          />
          <GroupPanel
            visible
            allowColumnDragging
          />
          <HeaderFilter
            visible
          >
            <Search enabled />
          </HeaderFilter>
          <FilterRow visible />
          <FilterPanel visible />
          <Column
            dataField="Status"
            dataType="number"
            caption="Status"
            editCellRender={this.selectBoxSatusColumnTemplate}
          >
            <Lookup dataSource={this.statusData} valueExpr="Value" displayExpr="Text" />
          </Column>
          <Column
            dataField="Type"
            dataType="string"
            caption="Type"
            visible={false}
          />
          <Column
            dataField="CallDate"
            dataType="datetime"
            caption="Call Date"
            format={appConst.dateAndTimeDisplayFormat}
            sortOrder="desc"
            calculateCellValue={
              (data) => {
                return convertToTZ(data.CallDate);
              }
            }
          />
          <Column
            dataField="CallDateYear"
            dataType="string"
            allowEditing={false}
            formItem={{ visible: false }}
            visible={false}
          />
          <Column
            dataField="CallDateMonth"
            dataType="string"
            allowEditing={false}
            formItem={{ visible: false }}
            visible={false}
          />
          <Column
            dataField="PreferredFirstName"
            dataType="string"
            caption="First Name"
            visible={!this.iSPersonGrid() || !!this.props.isInvolvedCalls}
            showInColumnChooser={!this.iSPersonGrid() || !!this.props.isInvolvedCalls}
            formItem={{ visible: !this.iSPersonGrid() || !!this.props.isInvolvedCalls }}
            cellRender={(data) => this.renderPersonNameLink(renderPreferredFirstNameGridCell(this, data), data.data)}
          />
          <Column
            dataField="LastName"
            dataType="string"
            caption="Last Name"
            visible={!this.iSPersonGrid() || !!this.props.isInvolvedCalls}
            showInColumnChooser={!this.iSPersonGrid() || !!this.props.isInvolvedCalls}
            formItem={{ visible: !this.iSPersonGrid() || !!this.props.isInvolvedCalls }}
            cellRender={(data) => this.renderPersonNameLink(data.data.LastName, data.data)}
          />
          <Column dataField="School" dataType="string" caption="School" allowEditing="false" />

          <Column
            dataField="CategoriesDisplay"
            dataType="string"
            caption="Categories"
            calculateFilterExpression={(value) => {
              if (!value) return null;
              try {
                const selection = JSON.parse(value);
                if (!selection?.length) return null;
                const exprs = selection.map(x => {
                  const text = x.subCategoryId
                    ? `${x.categoryText}(${x.subCategoryText})`
                    : x.categoryText;
                  return ['CategoriesDisplay', 'contains', text];
                });
                return exprs.length === 1 ? exprs[0] : exprs.reduce((acc, expr) => [acc, 'or', expr]);
              } catch {
                return ['CategoriesDisplay', 'contains', value];
              }
            }}
          />




          <Column
            dataField="CallLogActionId"
            dataType="number"
            caption="Action"
            editCellRender={this.selectBoxActionColumnTemplate}
          >
            <Lookup dataSource={this.actionsData.store()} valueExpr="Value" displayExpr="Text" />
          </Column>
          <Column dataField="UserId" dataType="number" caption="User">
            <Lookup dataSource={this.usersData.store()} valueExpr="Id" displayExpr="Name" />
          </Column>
          <Column
            dataField="InvolvedPeopleDisplay"
            dataType="string"
            caption="Involved People"
            allowEditing={false}
            visible={false}
          />
          <Column
            dataField="Details"
            dataType="string"
            caption="Details"
            cellRender={this.renderDetails}
            width={250}
          />
          <Column type="buttons">
            <Button
              icon="edit"
              hint="Edit Call Log"
              onClick={(e) => {
                this.setState((prev) => ({
                  callLogPopups: [...prev.callLogPopups, { key: Date.now(), Id: e.row.key }],
                }));
              }}
            />
            <Button name="delete" />
          </Column>
        </DataGrid >
        {this.renderMemberEditPopUp()}
        {this.renderNonMemberEditPopUp()}
        {this.state.callLogPopups.map((popup, index) => (
          <CallLogPopup
            key={popup.key}
            visible
            Id={popup.Id}
            hide={() => this.setState((prev) => ({
              callLogPopups: prev.callLogPopups.filter((p) => p.key !== popup.key),
            }))}
            gridEditing={true}
            offset={`${50 * index} ${50 * index}`}
            refreshCallLogDropDownButton={() => {}}
          />
        ))}
      </>
    );
  }
}

export function PersonCallLogGrid(props) {
  const lookups = useContext(CallLogLookupsContext);
  return <PersonCallLogGridClass {...props} lookups={lookups} />;
}
